/**
 * HTTP Polling Hooks for Console (Fallback Mode)
 * 
 * These hooks provide two modes:
 * 1. WebTransport mode: Uses SDK subscriptions for real-time updates
 * 2. HTTP fallback mode: Uses polling against edge.estream.dev REST endpoints
 * 
 * The hooks automatically detect transport state and switch modes.
 * 
 * When #191 (WebTransport server) is deployed, these hooks will automatically
 * use the faster WebTransport subscriptions.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  useConnection, 
  useNetworkMetrics as useSdkNetworkMetrics,
  useTopology as useSdkTopology,
  useCircuits as useSdkCircuits,
} from '@estream/sdk-browser/react';

// Edge proxy URL for HTTP polling
const EDGE_URL = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';

// Check if WebTransport is likely available (not in HTTP-only mode)
export function useIsWebTransportAvailable(): boolean {
  const { state } = useConnection();
  // Connected state means WebTransport worked
  return state === 'connected';
}

// Export connection state for UI display
export function useTransportMode(): { mode: 'webtransport' | 'http-polling' | 'disconnected'; state: string } {
  const { state } = useConnection();
  if (state === 'connected') {
    return { mode: 'webtransport', state };
  }
  if (state === 'connecting') {
    return { mode: 'disconnected', state };
  }
  // When WebTransport fails (disconnected, error), we use HTTP polling
  return { mode: 'http-polling', state };
}

// =============================================================================
// Types (matching SDK types)
// =============================================================================

export interface NetworkMetrics {
  timestamp: number;
  totalNodes: number;
  totalConnections: number;
  messagesPerSecond: string | bigint;
  avgLatencyMs: number;
  activeCircuits: number;
  pendingProposals: number;
  totalStorageBytes: string | bigint;
}

export interface TopologyNode {
  id: string;
  type: string;
  status: string;
  region: string;
  cloud: string;
  publicIp: string | null;
  privateIp: string | null;
  zone: string | null;
  version: string | null;
  uptimeSeconds: string | null;
  lastHeartbeat: number | null;
  metrics: {
    cpuPercent: number;
    memoryPercent: number;
    connections: number;
    messagesPerSecond: number;
  } | null;
}

export interface TopologySnapshot {
  timestamp: number;
  version: string;
  nodes: TopologyNode[];
  links: Array<{
    id: string;
    source: string;
    target: string;
    type: string;
    status: string;
  }>;
}

export interface CircuitsState {
  pending: unknown[];
  completed: unknown[];
  deployed: Array<{
    circuitId: string;
    type: string;
    version: string;
    environment: string;
    deployedAt: number;
  }>;
}

// =============================================================================
// Generic Polling Hook
// =============================================================================

export interface UsePollingResult<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | undefined;
  refresh: () => void;
}

export function usePolling<T>(
  endpoint: string,
  intervalMs: number = 5000
): UsePollingResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (retryCount = 0) => {
    const url = `${EDGE_URL}${endpoint}`;
    const maxRetries = 2;
    
    try {
      console.debug(`[Polling] Fetching ${url}${retryCount > 0 ? ` (retry ${retryCount})` : ''}`);
      
      // Use XMLHttpRequest for Safari compatibility (fetch has issues with some cross-origin requests)
      const json = await new Promise<unknown>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.timeout = 15000;
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid JSON response'));
            }
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Request timeout'));
        xhr.send();
      });
      
      console.debug(`[Polling] Success for ${endpoint}:`, json);
      setData(json as T);
      setError(undefined);
      setLoading(false);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e);
      console.error(`[Polling] Error fetching ${url}:`, errorMsg);
      
      // Retry on network errors
      if (retryCount < maxRetries && (
        errorMsg.includes('Network error') || 
        errorMsg.includes('timeout') ||
        errorMsg.includes('Load failed')
      )) {
        const delay = 1000 * (retryCount + 1);
        console.log(`[Polling] Retrying ${endpoint} in ${delay}ms...`);
        setTimeout(() => fetchData(retryCount + 1), delay);
        return;
      }
      
      setError(e instanceof Error ? e : new Error(String(e)));
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    // Initial fetch (with retry support)
    fetchData(0);

    // Start polling (no retry on interval - next poll will retry if needed)
    intervalRef.current = setInterval(() => fetchData(0), intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [endpoint, intervalMs, fetchData]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh };
}

// =============================================================================
// Console-Specific Hooks
// =============================================================================

/**
 * Poll network metrics from edge proxy (HTTP fallback only)
 */
export function useNetworkMetricsPolling(intervalMs = 5000): UsePollingResult<NetworkMetrics> {
  return usePolling<NetworkMetrics>('/console/metrics/network', intervalMs);
}

/**
 * Poll topology snapshot from edge proxy (HTTP fallback only)
 */
export function useTopologyPolling(intervalMs = 10000): UsePollingResult<TopologySnapshot> {
  return usePolling<TopologySnapshot>('/console/topology/snapshot', intervalMs);
}

/**
 * Poll circuits state from edge proxy (HTTP fallback only)
 */
export function useCircuitsPolling(intervalMs = 10000): UsePollingResult<CircuitsState> {
  return usePolling<CircuitsState>('/console/circuits/state', intervalMs);
}

/**
 * Poll governance state from edge proxy (HTTP fallback only)
 */
export function useGovernancePolling(intervalMs = 10000) {
  return usePolling<{
    pending: unknown[];
    active: unknown[];
    completed: unknown[];
  }>('/console/governance/state', intervalMs);
}

// =============================================================================
// Additional API Hooks (#179 - Feature Parity)
// =============================================================================

export interface IdentityRecord {
  id: string;
  displayName: string;
  publicKeyHash: string;
  roles: string[];
  sessions: number;
  lastActive: string;
  createdAt: string;
}

export interface SessionRecord {
  id: string;
  deviceName: string;
  lastActive: string;
  ipAddress: string;
  current: boolean;
}

export interface ApprovalRecord {
  id: string;
  type: string;
  title: string;
  description: string;
  initiator: string;
  status: 'pending' | 'approved' | 'rejected';
  signatures: string[];
  requiredSignatures: number;
  createdAt: string;
  expiresAt: string;
}

export interface LatticeRecord {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'error';
  region: string;
  nodeCount: number;
  createdAt: string;
}

export interface BridgeRecord {
  id: string;
  type: 'deposit' | 'withdraw';
  amount: string;
  asset: string;
  status: 'pending' | 'confirmed' | 'failed';
  txHash?: string;
  createdAt: string;
}

/**
 * Fetch identities from edge proxy
 */
export function useIdentities(providerId = 'default') {
  return usePolling<{ identities: IdentityRecord[] }>(`/api/identity?providerId=${providerId}`, 10000);
}

/**
 * Fetch sessions from edge proxy
 */
export function useSessions() {
  return usePolling<{ sessions: SessionRecord[] }>('/api/identity/sessions', 10000);
}

/**
 * Fetch pending approvals from edge proxy
 */
export function useApprovals() {
  return usePolling<{ actions: ApprovalRecord[] }>('/api/governance/list?status=pending', 10000);
}

/**
 * Fetch lattice configurations
 */
export function useLattices() {
  // For now, return mock until lattice API is implemented
  const [data] = useState<{ lattices: LatticeRecord[] }>({
    lattices: [
      {
        id: 'alpha-devnet',
        name: 'Alpha Devnet',
        status: 'running',
        region: 'us-central',
        nodeCount: 1,
        createdAt: new Date().toISOString(),
      },
    ],
  });
  return { data, loading: false, error: undefined, refresh: () => {} };
}

/**
 * Fetch bridge transactions
 */
export function useBridgeTransactions() {
  // For now, return empty until bridge API is implemented
  const [data] = useState<{ transactions: BridgeRecord[] }>({ transactions: [] });
  return { data, loading: false, error: undefined, refresh: () => {} };
}

// =============================================================================
// Smart Hooks (Auto-switch between WebTransport and HTTP Polling)
// =============================================================================

/**
 * Smart network metrics hook
 * - Uses WebTransport subscription when available
 * - Falls back to HTTP polling when WebTransport fails
 */
export function useNetworkMetrics(): UsePollingResult<NetworkMetrics> {
  const isWebTransportAvailable = useIsWebTransportAvailable();
  const sdkResult = useSdkNetworkMetrics();
  const pollingResult = useNetworkMetricsPolling(5000);
  
  // Use SDK result if WebTransport is connected
  if (isWebTransportAvailable && sdkResult.data) {
    return {
      data: sdkResult.data as NetworkMetrics,
      loading: sdkResult.loading,
      error: sdkResult.error,
      refresh: sdkResult.refresh,
    };
  }
  
  // Fall back to HTTP polling
  return pollingResult;
}

/**
 * Smart topology hook
 * - Uses WebTransport subscription when available
 * - Falls back to HTTP polling when WebTransport fails
 */
export function useTopology(): UsePollingResult<TopologySnapshot> {
  const isWebTransportAvailable = useIsWebTransportAvailable();
  const sdkResult = useSdkTopology();
  const pollingResult = useTopologyPolling(10000);
  
  if (isWebTransportAvailable && sdkResult.data) {
    // Convert SDK type to local type (version: bigint -> string)
    const sdkData = sdkResult.data;
    return {
      data: {
        ...sdkData,
        version: String(sdkData.version),
      } as TopologySnapshot,
      loading: sdkResult.loading,
      error: sdkResult.error,
      refresh: sdkResult.refresh,
    };
  }
  
  return pollingResult;
}

/**
 * Smart circuits hook
 * - Uses WebTransport subscription when available
 * - Falls back to HTTP polling when WebTransport fails
 */
export function useCircuits(): UsePollingResult<CircuitsState> {
  const isWebTransportAvailable = useIsWebTransportAvailable();
  const sdkResult = useSdkCircuits();
  const pollingResult = useCircuitsPolling(10000);
  
  if (isWebTransportAvailable && sdkResult.data) {
    return {
      data: sdkResult.data as CircuitsState,
      loading: sdkResult.loading,
      error: sdkResult.error,
      refresh: sdkResult.refresh,
    };
  }
  
  return pollingResult;
}

/**
 * Smart bridge hook
 * - Uses HTTP polling for bridge transactions
 */
export function useBridge(): UsePollingResult<{ transactions: BridgeRecord[] }> {
  return useBridgeTransactions();
}
